import React, { useMemo } from 'react';
import {
  Pencil,
  Trash2,
  Mail,
  Phone,
  MapPin,
  Clock,
  CalendarDays,
  AlertCircle,
  TrendingUp,
  Receipt,
  Target,
  Home
} from 'lucide-react';
import { getWorkerMaxDailyInterventions } from '../../utils/workerUtils';
import { computeWorkerMonthStats } from '../../utils/workerStatsUtils';
import {
  calculateWorkerRating,
  getRatingBarColorClass,
  getRatingColorClass
} from '../../utils/workerRatingUtils';
import WorkerMonthPicker from './WorkerMonthPicker';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('sr-RS');

const StatCell = ({ icon: Icon, label, value, sub, iconClass = 'text-gray-400' }) => (
  <div className="rounded-md bg-gray-900/45 border border-gray-700/50 px-2 py-1.5 min-w-0">
    <div className="flex items-center gap-1 mb-0.5">
      <Icon className={`h-3 w-3 flex-shrink-0 ${iconClass}`} />
      <span className="text-[10px] text-gray-500 truncate">{label}</span>
    </div>
    <p className="text-xs font-semibold text-white tabular-nums truncate">{value}</p>
    {sub && <p className="text-[10px] text-gray-500 truncate">{sub}</p>}
  </div>
);

const WorkerStatsCard = ({
  worker,
  archiveJobs,
  monthKey,
  onMonthChange,
  ratingParams,
  editingMaxWorkerId,
  maxDailyDraft,
  savingMaxWorkerId,
  onStartEditMaxDaily,
  onCancelEditMaxDaily,
  onSaveMaxDaily,
  onMaxDailyDraftChange,
  onEdit,
  onDelete
}) => {
  const stats = useMemo(
    () => computeWorkerMonthStats(worker, archiveJobs, monthKey),
    [worker, archiveJobs, monthKey]
  );

  const { score } = useMemo(
    () => calculateWorkerRating(stats, worker, ratingParams),
    [stats, worker, ratingParams]
  );

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700/50 shadow-lg overflow-hidden">
      {/* Zaglavlje */}
      <div className="flex justify-between items-start gap-2 p-3 border-b border-gray-700/60">
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
            style={{ backgroundColor: worker.color || '#3B82F6' }}
          >
            {worker.firstName?.charAt(0)}
            {worker.lastName?.charAt(0)}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-gray-500 tracking-wide mb-0.5">Mesečni učinak</p>
            <h3 className="font-semibold text-sm text-white truncate">
              {worker.firstName} {worker.lastName}
            </h3>
            <p className="text-[11px] text-gray-400 truncate">
              {worker.specialization || '—'}
              {worker.specializationCoefficient ? ` · koef. ${worker.specializationCoefficient}` : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={() => onEdit(worker)}
            className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-gray-700 rounded transition-colors"
            title="Izmeni radnika"
          >
            <Pencil size={15} />
          </button>
          <button
            type="button"
            onClick={() => onDelete(worker._id)}
            className="p-1.5 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded transition-colors"
            title="Obriši radnika"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Kontakt */}
      <div className="px-3 py-2 space-y-1.5 border-b border-gray-700/40 bg-gray-900/20">
        <div className="flex items-center gap-2 min-w-0 text-[11px] text-gray-300">
          <Mail className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
          <span className="truncate" title={worker.email}>{worker.email}</span>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0 text-[11px] text-gray-300">
            <Phone className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
            <span className="truncate">{worker.phone}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-gray-500">Maks./dan:</span>
            {editingMaxWorkerId === worker._id ? (
              <>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={maxDailyDraft}
                  onChange={(e) => onMaxDailyDraftChange(e.target.value)}
                  className="w-12 px-1 py-0.5 rounded bg-gray-700 border border-gray-600 text-white text-xs"
                  disabled={savingMaxWorkerId === worker._id}
                />
                <button
                  type="button"
                  onClick={() => onSaveMaxDaily(worker)}
                  className="text-green-400 text-[10px] font-medium"
                  disabled={savingMaxWorkerId === worker._id}
                >
                  OK
                </button>
                <button
                  type="button"
                  onClick={onCancelEditMaxDaily}
                  className="text-gray-400 text-[10px]"
                  disabled={savingMaxWorkerId === worker._id}
                >
                  ×
                </button>
              </>
            ) : (
              <>
                <span className="font-semibold text-white tabular-nums">
                  {getWorkerMaxDailyInterventions(worker)}
                </span>
                <button
                  type="button"
                  onClick={() => onStartEditMaxDaily(worker)}
                  className="text-blue-400 hover:text-blue-300"
                  title="Izmeni limit"
                >
                  <Pencil size={11} />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mesec */}
      <div className="px-3 pt-2">
        <WorkerMonthPicker monthKey={monthKey} onChange={onMonthChange} />
      </div>

      {/* Rejting */}
      <div className="px-3 py-2">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-[11px] text-gray-400">Rejting</span>
          <span className={`text-sm font-bold tabular-nums ${getRatingColorClass(score)}`}>
            {stats.completedJobs === 0 ? '—' : `${score}/100`}
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-900 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${getRatingBarColorClass(score)}`}
            style={{ width: `${stats.completedJobs === 0 ? 0 : score}%` }}
          />
        </div>
      </div>

      {/* Mesečna statistika */}
      <div className="px-3 pb-3 grid grid-cols-3 gap-1.5">
        <StatCell icon={CalendarDays} label="Završeni" value={stats.completedJobs} iconClass="text-blue-400" />
        <StatCell icon={MapPin} label="1. izlazak" value={stats.firstVisitJobs} iconClass="text-green-400" />
        <StatCell icon={Clock} label="Ponovni" value={stats.secondVisitJobs} iconClass="text-amber-400" />
        <StatCell icon={TrendingUp} label="Prihod" value={fmt(stats.totalRevenue)} iconClass="text-emerald-400" />
        <StatCell icon={Receipt} label="Troškovi" value={fmt(stats.totalExpenses)} iconClass="text-orange-400" />
        <StatCell icon={AlertCircle} label="Reklamacije" value={stats.complaintsCount} iconClass="text-amber-400" />
        <StatCell
          icon={Home}
          label="Adrese"
          value={stats.uniqueAddresses}
          sub={`${stats.activeDays} dana`}
          iconClass="text-violet-400"
        />
        <StatCell
          icon={Target}
          label="Kapacitet"
          value={`${stats.completedJobs}/${stats.monthMaxCapacity}`}
          iconClass="text-cyan-400"
        />
        <div className="rounded-md bg-gray-900/45 border border-gray-700/50 px-2 py-1.5 flex items-center justify-center">
          <span
            className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
              worker.active ? 'bg-green-900/40 text-green-400' : 'bg-red-900/40 text-red-400'
            }`}
          >
            {worker.active ? 'Aktivan' : 'Neaktivan'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default WorkerStatsCard;
